import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react"; // import { Transition } from "react-transition-group";
import { Transition } from "react-transition-group";
import classnames from "classnames";
import collapsibleContentCss from "./CollapsibleContent.module.scss";
import { useResizeDetector } from "react-resize-detector";

const TransitionTimeouts = {
    Fade: 150, // $transition-fade
    Collapse: 350, // $transition-collapse
    Modal: 300, // $modal-transition
    Carousel: 600, // $carousel-transition
    Slow: 100,
};

export const transitionStatuses = {
    EXPANDING: "entering",
    EXPANDED: "entered",
    COLLAPSING: "exiting",
    COLLAPSED: "exited",
};

const transitionStatusToClassHash = {
    [transitionStatuses.EXPANDING]: "expanding",
    [transitionStatuses.EXPANDED]: "expanded",
    [transitionStatuses.COLLAPSING]: "collapsing",
    [transitionStatuses.COLLAPSED]: "collapsed",
};

export function mapToCssModules(className = "", cssModule) {
    if (!cssModule) {
        return className;
    }
    return className
        .split(" ")
        .map((c) => cssModule[c] || c)
        .join(" ");
}

function getTransitionClass(status) {
    return transitionStatusToClassHash[status] || "collapsed";
}

function getHeight(node) {
    return node?.scrollHeight;
}

export default function CollapsibleContentNew(props) {
    const {
        isOpen = false,
        fade = true,
        fast = true,
        slow = false,
        appear = false,
        enter = true,
        exit = true,
        instant,
        className,
        style,
        statusToClassNameMap = {},
        timeout = TransitionTimeouts.Collapse,
        children,
        ...otherProps
    } = props;

    const nodeRef = useRef(null);
    const nodeRefInner = useRef(null);
    const { height: childrenHeight } = useResizeDetector({ targetRef: nodeRefInner });
    const [height, setHeight] = useState(null);

    useEffect(() => {
        if (childrenHeight && isOpen) {
            setHeight(childrenHeight);
        }
    }, [childrenHeight, height, isOpen]);

    const [initiatedHeight, setInitiatedHeight] = useState(false);

    useEffect(() => {
        if (!initiatedHeight && nodeRef.current) {
            if (isOpen) {
                setHeight(getHeight(nodeRef?.current));
            }
            setInitiatedHeight(true);
        }
    }, [nodeRef, isOpen, initiatedHeight]);

    const onEntering = useCallback(() => {
        setHeight(getHeight(nodeRef?.current));
    }, []);

    const onEntered = useCallback(() => {
        // setHeight(null);
    }, []);

    const onExit = useCallback(() => {
        setHeight(getHeight(nodeRef?.current));
    }, []);

    const onExiting = useCallback(() => {
        // getting this variable triggers a reflow
        setHeight(0);
    }, []);

    const onExited = useCallback(() => {
        setHeight(null);
    }, []);

    const transitionProps = {
        timeout,
        appear,
        enter,
        exit,
    };

    return (
        <Transition nodeRef={nodeRef} {...transitionProps} in={!!isOpen} onEntering={onEntering} onEntered={onEntered} onExit={onExit} onExiting={onExiting} onExited={onExited}>
            {(status) => {
                let collapseClass = getTransitionClass(status);
                const classes = mapToCssModules(
                    classnames(
                        className,
                        statusToClassNameMap[status],
                        fade && collapsibleContentCss.fade,
                        fast && collapsibleContentCss.fast,
                        instant && collapsibleContentCss.instant,
                        slow && collapsibleContentCss.slow,
                        collapseClass,
                    ),
                    collapsibleContentCss,
                );
                let childrenStyle = {
                    ...style,
                    ...(height === null ? null : { height }),
                };

                return (
                    <div {...otherProps} style={childrenStyle} className={classes} ref={nodeRef}>
                        <div ref={nodeRefInner}>{children}</div>
                    </div>
                );
            }}
        </Transition>
    );
}
